import { isWordChar } from "./words";

type CacheData = {
  isBlock?: boolean;
  style?: CSSStyleDeclaration;
};

class TextWalker {
  constructor(
    private readonly walker: TreeWalker,
    private readonly cache: Cache,
    private offset: number
  ) {}

  public currentNode(): Node {
    return this.walker.currentNode;
  }

  public currentOffset(): number {
    return this.offset;
  }

  public walkLeft(charMatcher: CharMatcher): void {
    this.walk(
      "previousSibling",
      (text) => {
        while (this.offset > 0) {
          if (charMatcher(text[this.offset - 1])) {
            this.offset--;
          } else {
            return true;
          }
        }
        return false;
      },
      (text) => text.length
    );
  }

  public walkRight(charMatcher: CharMatcher): void {
    this.walk(
      "nextSibling",
      (text) => {
        while (this.offset < text.length) {
          if (charMatcher(text[this.offset])) {
            this.offset++;
          } else {
            return true;
          }
        }
        return false;
      },
      (_text) => 0
    );
  }

  private walk(
    direction: "previousSibling" | "nextSibling",
    moveOffset: (text: string) => boolean,
    nextOffset: (text: Text) => number
  ): void {
    while (true) {
      const node = this.walker.currentNode;
      if (moveOffset(node.textContent)) {
        return;
      }

      const sibling = this.walker[direction]();
      if (!sibling) return;
      if (this.cache.isBlock(sibling)) {
        this.walker.currentNode = node;
        return;
      }
      this.offset = nextOffset(sibling as Text);
    }
  }
}

class Cache {
  private data = new Map<Node, CacheData>();

  public acceptNode(node: Node): number {
    if (this.isBlock(node) || node.nodeType === Node.TEXT_NODE) {
      return NodeFilter.FILTER_ACCEPT;
    } else {
      return NodeFilter.FILTER_SKIP;
    }
  }

  public isBlock(node: Node): boolean {
    const data = this.get(node);
    if (typeof data.isBlock === "boolean") {
      return data.isBlock;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      data.isBlock = false;
      return false;
    }

    const wnd = node.ownerDocument?.defaultView || window;
    const style =
      data.style || (data.style = wnd.getComputedStyle(node as Element));

    if (style.display === "block" || style.display === "flex") {
      return (data.isBlock = true);
    }

    return (data.isBlock = false);
  }

  private get(node: Node): CacheData {
    if (!this.data.has(node)) {
      this.data.set(node, {});
    }
    return this.data.get(node)!;
  }
}

type CharMatcher = (c: string) => boolean;

function findParentBlock(node: Node, cache: Cache): Node | null {
  if (cache.isBlock(node)) {
    return node;
  }

  const doc = node.ownerDocument || window.document;
  const tw = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, cache);
  tw.currentNode = node;
  // the filter will ensure we get the first parent block element
  return tw.parentNode();
}

function buildWalker(ref: Node, cache: Cache): TreeWalker {
  const doc = ref.ownerDocument || window.document;
  const root = findParentBlock(ref, cache) || doc.body;

  return doc.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    cache
  );
}

export function expandRangeToWord(
  range: Range,
  cache: Cache = new Cache()
): Range {
  range = range.cloneRange();
  const tw = buildWalker(range.commonAncestorContainer, cache);

  tw.currentNode = range.startContainer;
  const leftWalker = new TextWalker(tw, cache, range.startOffset);
  leftWalker.walkLeft(isWordChar);
  range.setStart(leftWalker.currentNode(), leftWalker.currentOffset());

  tw.currentNode = range.endContainer;
  const rightWalker = new TextWalker(tw, cache, range.endOffset);
  rightWalker.walkRight(isWordChar);
  range.setEnd(rightWalker.currentNode(), rightWalker.currentOffset());

  return range;
}

export function retrieveBefore(
  range: Range,
  maxChars: number = 100,
  cache: Cache = new Cache()
): string {
  const document = range.startContainer.ownerDocument || window.document;
  const beforeRange = document.createRange();
  beforeRange.setEnd(range.startContainer, range.startOffset);

  const { node, offset } = retrieveAround(
    range.startContainer,
    range.startOffset,
    "walkLeft",
    maxChars,
    cache
  );
  beforeRange.setStart(node, offset);

  return beforeRange.toString();
}

export function retrieveAfter(
  range: Range,
  maxChars: number = 100,
  cache: Cache = new Cache()
): string {
  const document = range.startContainer.ownerDocument || window.document;
  const afterRange = document.createRange();
  afterRange.setStart(range.endContainer, range.endOffset);

  const { node, offset } = retrieveAround(
    range.endContainer,
    range.endOffset,
    "walkRight",
    maxChars,
    cache
  );
  afterRange.setEnd(node, offset);

  return afterRange.toString();
}

function retrieveAround(
  start: Node,
  offset: number,
  direction: "walkLeft" | "walkRight",
  maxChars: number,
  cache: Cache
): { node: Node; offset: number } {
  const charMatcher: CharMatcher = (c) => maxChars-- > 0;

  const tw = buildWalker(start, cache);
  tw.currentNode = start;
  const walker = new TextWalker(tw, cache, offset);
  walker[direction](charMatcher);
  return {
    node: walker.currentNode(),
    offset: walker.currentOffset(),
  };
}
